import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { platformLayoutOptions$ } from "$root/components/PlatformRouter/layout-options";
import ReactQueryWrapper from "$root/components/ReactQueryWrapper";
import ReviewCompositionErrors from "$root/components/ReviewCompositionErrors";
import { aclByArea } from "$root/functional-areas/acl/checkers/all";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import {
	type CompositionInRetrievingDataResponse,
	getCompositionDataInRetrievingData,
} from "$root/functional-areas/instruments/repository";
import { SmallInvestmentSummary } from "$root/functional-areas/portfolio/SmallInvestmentSummary";
import { useUserValue } from "$root/functional-areas/user";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { Card } from "$root/widgets-architecture/layout/Card";
import WidgetsGrid, { type WidgetGridHandles } from "$root/widgets-architecture/layout/WidgetsGrid";
import { ActionText, Banner, Text } from "@mdotm/shadcn/legacy/components";
import { useAtom } from "@mdotm/shadcn/legacy/reactivity";
import { type FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { GridName } from "./types";
import type { InvestmentSummaryWithRequiredUuid } from "./common";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import type { ImperativeHandleRefProps } from "@mdotm/shadcn/legacy/react-extensions";

type OverviewProps = {
	portfolio: InvestmentSummaryWithRequiredUuid;
	isEnhanceSelected: boolean;
	gridName: GridName;
	/** Validated `?asOf` date; the summary shows its values as of that day. */
	asOf?: string;
};

type PortfolioComposition2Props = {
	portfolio: InvestmentSummaryWithRequiredUuid;
	gridName: GridName;
	isEnhanceSelected: boolean;
};

const PortfolioComposition2: FC<PortfolioComposition2Props & ImperativeHandleRefProps<WidgetGridHandles>> = ({
	portfolio,
	gridName,
	handleRef,
}) => {
	const { status, action, reference, richAcl } = portfolio ?? {};

	const user = useUserValue();
	const { push } = useTypedNavigation();

	const canEditComposition = aclByArea.portfolio.canEditComposition(user.id, richAcl?.acl ?? []);

	if (action === "OPTIMIZATION" && canEditComposition) {
		return (
			<div className="flex flex-col justify-center items-center">
				<h2>Page no longer supported if you want to edit composition go to</h2>
				<ActionText onClick={() => push("Portfolios/ManualEditPortfolio", { uuid: portfolio.uuid })}>
					Approve proposal
				</ActionText>
			</div>
		);
	}

	if (status === "RETRIEVING_DATA") {
		return (
			<ReactQueryWrapper
				queryFn={() =>
					getCompositionDataInRetrievingData(portfolio.uuid, reference ? "TARGET_INVESTMENT" : "INVESTMENT")
				}
				queryKey={["portfolioComposition", portfolio.uuid, portfolio?.status]}
			>
				{(response) => <PortfolioReviewBlock portfolio={portfolio} {...response} />}
			</ReactQueryWrapper>
		);
	}

	return <WidgetsGrid gridName={gridName} handleRef={handleRef} />;
};

const PortfolioReviewBlock = ({
	portfolio,
	instruments,
	compositionWithCash,
}: CompositionInRetrievingDataResponse & {
	portfolio: InvestmentSummaryWithRequiredUuid;
}) => {
	const { status } = portfolio ?? {};
	const { t } = useTranslation();

	const [platformLayoutOptions, setPlatformLayoutOptions] = useAtom(platformLayoutOptions$);
	const platformLayoutOptionsRef = useRef({ platformLayoutOptions, setPlatformLayoutOptions });
	const columns = columnsDefinition.useInstrumentComposition("read");
	useEffect(() => {
		const scopePlatformLayoutOptions = platformLayoutOptionsRef.current;
		scopePlatformLayoutOptions.setPlatformLayoutOptions({
			...scopePlatformLayoutOptions.platformLayoutOptions,
			maxPageHeight: "screen",
		});

		return () => {
			scopePlatformLayoutOptions.setPlatformLayoutOptions({
				...scopePlatformLayoutOptions.platformLayoutOptions,
				maxPageHeight: "auto",
			});
		};
	}, []);

	return (
		<Card>
			<Text type="Body/XL/Bold" className="mb-4">
				Composition
			</Text>
			<ReviewCompositionErrors errors={compositionWithCash.uploadErrors} />
			<div className="mb-4" hidden={status === "RETRIEVING_DATA"}>
				<Banner severity="warning" title="Review Before Saving">
					Some instruments require your attention. This may be due to missing information or because they have been
					delisted. Please review the list below and make any necessary updates before saving.
				</Banner>
			</div>
			<div className="mb-4">
				<Banner severity="info">{t("PORTFOLIO_UPLOAD_ALERTS.CALCULATING_UPLOAD")}</Banner>
			</div>

			<AgGridWithModules<Instrument>
				{...agExtra.usePersistColState(persistedColStateKeys["tableV3-investment-composition-read"])}
				// The persisted column state can carry a rowGroup set from the Composition widget; without
				// a def of our own, the default auto-group column top-aligns the TagCell inner renderer
				// (its h-full can't stretch the inline .ag-group-value span — see ag-grid.css).
				autoGroupColumnDef={{ headerName: "Name", pinned: "left", cellClass: "composition-group-col" }}
				columnDefs={columns}
				rowData={instruments}
				getRowId={(x) => x.data.tickerName!}
			/>
		</Card>
	);
};

const Overview: FC<OverviewProps & ImperativeHandleRefProps<WidgetGridHandles>> = ({
	portfolio,
	isEnhanceSelected,
	gridName,
	asOf,
	handleRef,
}) => {
	return (
		<>
			<div className="mb-5">
				<SmallInvestmentSummary enhanced={isEnhanceSelected} portfolio={portfolio} asOf={asOf} />
			</div>
			<PortfolioComposition2
				portfolio={portfolio}
				gridName={gridName}
				handleRef={handleRef}
				isEnhanceSelected={isEnhanceSelected}
			/>
		</>
	);
};

export default Overview;
